import React from 'react';
import * as http from '../../helpers/http';

/**
 * LegisPocket — página pública de apresentação (pós-prospecção).
 *
 * Página puramente informativa: o prospectado já conversou com o time comercial
 * e recebe o link como material de apoio. Não há formulário nem captura de dados.
 *
 * HABILITAÇÃO — parametros_cliente, igual a HAB_IA e às demais HAB_*:
 *
 *   codigo = 'HAB_LEGISPOCKET'   valor = 'true' | 'false'   id_cliente = NULL
 *
 * A leitura vem de GET /config, que é rota pública (Schema.Public.routes.ts) e
 * devolve todo `HAB_%` já convertido para camelCase e booleano por
 * ControllerBase.getHabilitacoes() — daí o nome habLegispocket abaixo.
 *
 * Como é banco, e não variável de build, ligar/desligar não exige rebuild nem
 * redeploy: basta o UPDATE na tabela e um refresh na página.
 */

export const LEGISPOCKET_PATH = '/legispocket';

/** Nome da flag no retorno de /config (HAB_LEGISPOCKET -> habLegispocket). */
export const FLAG_LEGISPOCKET = 'habLegispocket';

export type EstadoLegisPocket = 'fora' | 'carregando' | 'liberado' | 'bloqueado';

/** True quando a URL atual é a rota pública do LegisPocket (com ou sem barra final). */
export function isRotaLegisPocket(pathname: string = window.location.pathname): boolean {
    const rota = String(pathname || '').replace(/\/+$/, '').toLowerCase();
    return rota === LEGISPOCKET_PATH;
}

/**
 * Resolve a habilitação da rota pública.
 *
 * Fora da rota não dispara requisição alguma — devolve 'fora' e o App segue o
 * fluxo normal. Na rota, consulta /config uma única vez.
 *
 * Falha de rede e flag ausente na tabela caem ambas em 'bloqueado': o padrão é
 * não renderizar o que não foi explicitamente liberado. Se a API está fora, a
 * plataforma inteira está fora e cair no login é o comportamento coerente.
 */
export function useLegisPocketLiberado(): EstadoLegisPocket {
    const naRota = isRotaLegisPocket();
    const [estado, setEstado] = React.useState<EstadoLegisPocket>(naRota ? 'carregando' : 'fora');

    React.useEffect(() => {
        if (!naRota) {
            setEstado('fora');
            return;
        }

        let ativo = true;
        setEstado('carregando');

        http.get('/config')
            .then(res => {
                if (!ativo) return;
                const liberado = res?.success === true && res?.data?.[FLAG_LEGISPOCKET] === true;
                setEstado(liberado ? 'liberado' : 'bloqueado');
            })
            .catch(() => { if (ativo) setEstado('bloqueado'); });

        return () => { ativo = false; };
    }, [naRota]);

    return estado;
}
